import {
    BoundingBox,
    Color,
    FlyController,
    Pose,
    Mat4,
    OrbitController,
    Vec2,
    Vec3
} from 'playcanvas';

import { AnimController } from './controllers/anim-controller.js';
import { easeOut } from './core/math.js';
import { AppController } from './input.js';
import { Picker } from './picker.js';

/** @import { InputController } from 'playcanvas' */

const pose = new Pose();

/**
 * Creates a rotation animation track around the object center
 * 키보드 이동 후에도 올바른 회전을 위해 수정됨
 *
 * @param {Pose} initial - The initial pose of the camera.
 * @param {Vec3} objectCenter - The center point of the object to rotate around.
 * @param {number} [keys] - The number of keys in the animation.
 * @param {number} [duration] - The duration of the animation in seconds.
 * @returns {object} - The animation track object containing position and target keyframes.
 */
const createRotateTrack = (initial, objectCenter, keys = 12, duration = 20) => {
    
    const times = new Array(keys).fill(0).map((_, i) => i / keys * duration);
    const position = [];
    const target = [];

    // 현재 카메라 위치에서 object center로의 벡터
    const radiusVector = new Vec3();
    radiusVector.sub2(initial.position, objectCenter);
    

    for (let i = 0; i < keys; ++i) {
        const angle = (i / keys) * 360; // 시계방향 회전
        const mat = new Mat4();
        mat.setFromEulerAngles(0, angle, 0);
        
        const rotatedPos = new Vec3();
        mat.transformPoint(radiusVector, rotatedPos);
        rotatedPos.add(objectCenter);

        // Position camera at the rotated position
        position.push(rotatedPos.x);
        position.push(rotatedPos.y);
        position.push(rotatedPos.z);

        // Always look at the object center
        target.push(objectCenter.x);
        target.push(objectCenter.y);
        target.push(objectCenter.z);
        
    }


    return {
        name: 'rotate',
        duration,
        frameRate: 1,
        target: 'camera',
        loopMode: 'repeat',
        interpolation: 'spline',
        keyframes: {
            times,
            values: {
                position,
                target
            }
        }
    };
};
class Viewer {
    constructor(app, entity, events, state, settings, params) {
        const { background, camera } = settings;
        const { graphicsDevice } = app;

        this.app = app;
        this.entity = entity;
        this.events = events;
        this.state = state;
        this.settings = settings;

        // disable auto render, we'll render only when camera changes
        app.autoRender = false;

        // apply camera animation settings
        entity.camera.clearColor = new Color(background.color);
        entity.camera.fov = camera.fov;

        // handle horizontal fov on canvas resize
        const updateHorizontalFov = () => {
            this.entity.camera.horizontalFov = graphicsDevice.width > graphicsDevice.height;
        };
        graphicsDevice.on('resizecanvas', () => {
            updateHorizontalFov();
            app.renderNextFrame = true;
        });
        updateHorizontalFov();

        // track camera changes
        const prevProj = new Mat4();
        const prevWorld = new Mat4();

        app.on('framerender', () => {
            const world = this.entity.getWorldTransform();
            const proj = this.entity.camera.projectionMatrix;
            const nearlyEquals = (a, b, epsilon = 1e-4) => {
                return !a.some((v, i) => Math.abs(v - b[i]) >= epsilon);
            };

            if (params.ministats) {
                app.renderNextFrame = true;
            }

            if (!app.autoRender && !app.renderNextFrame) {
                if (!nearlyEquals(world.data, prevWorld.data) ||
                    !nearlyEquals(proj.data, prevProj.data)) {
                    app.renderNextFrame = true;
                }
            }

            if (app.renderNextFrame) {
                prevWorld.copy(world);
                prevProj.copy(proj);
            }

            // suppress rendering till we're ready
            if (!state.readyToRender) {
                app.renderNextFrame = false;
            }
        });

        events.on('hqMode:changed', (value) => {
            graphicsDevice.maxPixelRatio = value ? window.devicePixelRatio : 1;
            app.renderNextFrame = true;
        });
        graphicsDevice.maxPixelRatio = state.hqMode ? window.devicePixelRatio : 1;
    }

    // initialize the viewer once gsplat asset is finished loading (so we know its bound etc)
    initialize() {
        const { app, entity, events, state, settings } = this;
    
        // get the gsplat
        const gsplat = app.root.findComponent('gsplat');
    
        // calculate scene bounding box
        const bbox = gsplat?.instance?.meshInstance?.aabb ?? new BoundingBox();
    
        // createRotateTrack 함수 정의 (initialize 함수 내부)
        const createRotateTrack = (initial, objectCenter, keys = 12, duration = 20) => {
            
            const times = new Array(keys).fill(0).map((_, i) => i / keys * duration);
            const position = [];
            const target = [];
    
            const radiusVector = new Vec3();
            radiusVector.sub2(initial.position, objectCenter);
            
    
            for (let i = 0; i < keys; ++i) {
                const angle = (i / keys) * 360;
                const mat = new Mat4();
                mat.setFromEulerAngles(0, angle, 0);
                
                const rotatedPos = new Vec3();
                mat.transformPoint(radiusVector, rotatedPos);
                rotatedPos.add(objectCenter);
    
                position.push(rotatedPos.x);
                position.push(rotatedPos.y);
                position.push(rotatedPos.z);
    
                target.push(objectCenter.x);
                target.push(objectCenter.y);
                target.push(objectCenter.z);
            }
    
            return {
                name: 'rotate',
                duration,
                frameRate: 1,
                target: 'camera',
                loopMode: 'repeat',
                interpolation: 'spline',
                keyframes: {
                    times,
                    values: {
                        position,
                        target
                    }
                }
            };
        };
    
        // 기존의 framePose, resetPose, userStart, animCamera, orbitCamera, flyCamera 생성 코드...
        const framePose = (() => {
            const sceneSize = bbox.halfExtents.length();
            const distance = sceneSize / Math.sin(entity.camera.fov / 180 * Math.PI * 0.5);
            return new Pose().look(
                new Vec3(2, 1, 2).normalize().mulScalar(distance).add(bbox.center),
                bbox.center
            );
        })();
    
        const resetPose = (() => {
            const { position, target } = settings.camera;
            return new Pose().look(
                new Vec3(position ?? [2, 1, 2]),
                new Vec3(target ?? [0, 0, 0])
            );
        })();
    
        const useReset = settings.camera.position || settings.camera.target || bbox.halfExtents.length() > 100;
        const userStart = (useReset ? resetPose : framePose).clone();
    
        let animCamera = (() => {
            const { animTracks, camera } = settings;
            if (animTracks?.length > 0 && camera.startAnim === 'animTrack') {
                const track = animTracks.find(track => track.name === camera.animTrack);
                if (track) {
                    return AnimController.fromTrack(track);
                }
            }
            return AnimController.fromTrack(createRotateTrack(userStart, bbox.center));
        })();
    
        // 3. OrbitController 설정 시 줌 범위 제한
        const orbitCamera = (() => {
            const orbitCamera = new OrbitController();
            
            // 바운딩 박스를 기준으로 최소 줌 거리 계산
            const boundingRadius = bbox.halfExtents.length();
            const minZoomDistance = boundingRadius * 1.2; // 바운딩 스피어보다 20% 더 멀리
            
            orbitCamera.zoomRange = new Vec2(minZoomDistance, Infinity);
            orbitCamera.pitchRange = new Vec2(-90, 90);
            orbitCamera.rotateDamping = 0.97;
            orbitCamera.moveDamping = 0.97;
            orbitCamera.zoomDamping = 0.97;
            
            
            return orbitCamera;
        })();
    
        const flyCamera = (() => {
            const flyCamera = new FlyController();
            flyCamera.pitchRange = new Vec2(-90, 90);
            flyCamera.rotateDamping = 0.97;
            flyCamera.moveDamping = 0.97;
            return flyCamera;
        })();
    
        const getCamera = (cameraMode) => {
            switch (cameraMode) {
                case 'orbit': return orbitCamera;
                case 'anim': return animCamera;
                case 'fly': return flyCamera;
            }
        };
    
        // 상태 설정
        state.hasAnimation = !!animCamera;
        state.animationDuration = animCamera ? animCamera.cursor.duration : 0;
        if (animCamera) {
            state.cameraMode = 'anim';
        }
    
        const activePose = new Pose();
        const controller = new AppController(app.graphicsDevice.canvas, entity.camera);
        controller.moveSpeed = Math.max(0.05, Math.min(1, bbox.halfExtents.length() * 0.0001)) * 200;
        
        // 스마트 카메라 조작 추적기
        const cameraInteractionTracker = {
            isInteracting: false,
            interactionStartTime: 0,
            lastInteractionType: null,
            interactionCount: 0,
            minDuration: 2000, // 최소 2초 이상 조작해야 기록
            lastSessionTime: 0,
            inactivityTimer: null,
            interactionTypes: new Set(), // 현재 세션에서 사용된 모든 조작 타입
            
            startInteraction(type) {
                const now = Date.now();
                
                if (!this.isInteracting) {
                    // 새로운 세션 시작
                    this.isInteracting = true;
                    this.interactionStartTime = now;
                    this.lastInteractionType = type;
                    this.interactionCount = 0;
                    this.interactionTypes.clear();
                    this.interactionTypes.add(type);
                    
                    
                    // 비활성 타이머 설정 (3초 후 자동 종료)
                    this.inactivityTimer = setTimeout(() => {
                        this.endInteraction();
                    }, 3000);
                } else {
                    // 기존 세션에 새로운 조작 타입 추가
                    this.interactionTypes.add(type);
                    this.lastInteractionType = type; // 가장 최근 타입으로 업데이트
                    
                    // 비활성 타이머 리셋
                    if (this.inactivityTimer) {
                        clearTimeout(this.inactivityTimer);
                        this.inactivityTimer = setTimeout(() => {
                            this.endInteraction();
                        }, 3000);
                    }
                }
                
                this.interactionCount++;
            },
            
            endInteraction() {
                if (this.isInteracting) {
                    this.isInteracting = false;
                    const duration = Date.now() - this.interactionStartTime;
                    const now = Date.now();
                    
                    // 타이머 정리
                    if (this.inactivityTimer) {
                        clearTimeout(this.inactivityTimer);
                        this.inactivityTimer = null;
                    }
                    
                    
                    // 최소 시간 체크 및 세션 간격 체크
                    if (duration >= this.minDuration && 
                        (now - this.lastSessionTime) > 12000) { // 12초 간격
                        
                        
                        if (window.analytics) {
                            try {
                                window.analytics.trackEvent('camera_interaction_session', { 
                                    type: this.lastInteractionType,
                                    types: Array.from(this.interactionTypes), // 사용된 모든 조작 타입
                                    duration: duration,
                                    interactionCount: this.interactionCount,
                                    cameraMode: state.cameraMode
                                });
                            } catch (error) {
                                console.warn('Failed to send analytics (non-critical):', error);
                            }
                        }
                        
                        this.lastSessionTime = now;
                    } else {
                    }
                }
            },
            
            // 주기적으로 비활성 상태 체크 (5초마다)
            checkInactivity() {
                if (this.isInteracting && 
                    (Date.now() - this.interactionStartTime) > 5000) {
                    this.endInteraction();
                }
            }
        };
        
        // 비활성 상태 체크를 위한 타이머
        setInterval(() => {
            cameraInteractionTracker.checkInactivity();
        }, 5000);
        
        // 주기적인 세션 전송 (10초마다 활성 세션이 있으면 전송)
        setInterval(() => {
            if (cameraInteractionTracker.isInteracting) {
                const duration = Date.now() - cameraInteractionTracker.interactionStartTime;
                if (duration >= 10000) { // 10초 이상 지속되면 전송
                    cameraInteractionTracker.endInteraction();
                }
            }
        }, 10000);
        
        // 마우스 이벤트 감지 (개선된 버전)
        const canvas = app.graphicsDevice.canvas;
        let isMouseDown = false;
        let lastMouseTime = 0;
        let mouseDownTime = 0;
        
        canvas.addEventListener('mousedown', (e) => {
            isMouseDown = true;
            mouseDownTime = Date.now();
            lastMouseTime = Date.now();
            
            if (state.cameraMode === 'orbit') {
                cameraInteractionTracker.startInteraction('orbit');
            }
        });
        
        // 마우스 업 이벤트 (더 안전하게)
        canvas.addEventListener('mouseup', (e) => {
            if (isMouseDown) {
                const clickDuration = Date.now() - mouseDownTime;
                isMouseDown = false;
                
                // 짧은 클릭은 무시 (500ms 미만)
                if (clickDuration < 500) {
                    return;
                }
                
                cameraInteractionTracker.endInteraction();
            }
        });
        
        // 마우스가 캔버스를 벗어날 때도 처리
        canvas.addEventListener('mouseleave', (e) => {
            if (isMouseDown) {
                const clickDuration = Date.now() - mouseDownTime;
                isMouseDown = false;
                
                // 짧은 클릭은 무시
                if (clickDuration < 500) {
                    return;
                }
                
                cameraInteractionTracker.endInteraction();
            }
        });
        
        canvas.addEventListener('mousemove', (e) => {
            if (isMouseDown && state.cameraMode === 'orbit') {
                cameraInteractionTracker.startInteraction('orbit');
            }
        });
        
        canvas.addEventListener('wheel', (e) => {
            if (state.cameraMode === 'orbit') {
                cameraInteractionTracker.startInteraction('zoom');
                // 휠 이벤트는 기존 세션에 추가만 하고 종료하지 않음
            }
        });
        
        // 터치 이벤트 감지 (개선된 핀치 감지)
        let touchStartTime = 0;
        let isTouching = false;
        let touchCount = 0;
        let initialDistance = 0;
        let lastDistance = 0;
        let isPinching = false;
        
        // 두 터치 포인트 사이의 거리 계산
        const getTouchDistance = (touches) => {
            if (touches.length < 2) return 0;
            const dx = touches[0].clientX - touches[1].clientX;
            const dy = touches[0].clientY - touches[1].clientY;
            return Math.sqrt(dx * dx + dy * dy);
        };
        
        // 터치 지원 확인
        
        canvas.addEventListener('touchstart', (e) => {
            touchStartTime = Date.now();
            isTouching = true;
            touchCount = e.touches.length;
            
            if (touchCount >= 2) {
                initialDistance = getTouchDistance(e.touches);
                lastDistance = initialDistance;
                isPinching = false; // 아직 핀치인지 확실하지 않음
            }
            
            // 초기에는 터치 개수만으로 판단
            const interactionType = state.cameraMode === 'fly' ? 'fly_touch' : 
                                   touchCount > 1 ? 'multi_touch' : 'orbit_touch';
            
            cameraInteractionTracker.startInteraction(interactionType);
        });
        
        canvas.addEventListener('touchmove', (e) => {
            if (isTouching) {
                const newTouchCount = e.touches.length;
                
                // 2개 이상의 터치일 때 핀치 감지
                if (newTouchCount >= 2) {
                    const currentDistance = getTouchDistance(e.touches);
                    const distanceChange = Math.abs(currentDistance - lastDistance);
                    
                    
                    // 거리 변화가 5px 이상이면 핀치로 판단 (임계값 낮춤)
                    if (distanceChange > 5) {
                        if (!isPinching) {
                            isPinching = true;
                            cameraInteractionTracker.startInteraction('pinch_zoom');
                        } else {
                            // 이미 핀치 중이면 계속 추적
                            cameraInteractionTracker.startInteraction('pinch_zoom');
                        }
                    } else if (!isPinching && distanceChange > 0 && distanceChange < 3) {
                        // 거리 변화가 거의 없으면 2손가락 팬(이동)
                        cameraInteractionTracker.startInteraction('two_finger_pan');
                    }
                    
                    // lastDistance를 항상 업데이트 (중요!)
                    lastDistance = currentDistance;
                } else if (newTouchCount !== touchCount) {
                    // 터치 개수가 변경되면 타입 업데이트
                    touchCount = newTouchCount;
                    isPinching = false;
                    const interactionType = state.cameraMode === 'fly' ? 'fly_touch' : 
                                           touchCount > 1 ? 'multi_touch' : 'orbit_touch';
                    cameraInteractionTracker.startInteraction(interactionType);
                }
            }
        });
        
        canvas.addEventListener('touchend', (e) => {
            if (e.touches.length === 0) {
                // 모든 터치가 끝났을 때
                isTouching = false;
                touchCount = 0;
                isPinching = false;
                initialDistance = 0;
                lastDistance = 0;
                // 비활성 타이머가 자동으로 종료하도록 둠
            } else {
                // 일부 터치만 끝났을 때 터치 개수 업데이트
                touchCount = e.touches.length;
                if (touchCount < 2) {
                    isPinching = false;
                    initialDistance = 0;
                    lastDistance = 0;
                }
            }
        });
        
        canvas.addEventListener('touchcancel', (e) => {
            isTouching = false;
            touchCount = 0;
            isPinching = false;
            initialDistance = 0;
            lastDistance = 0;
        });
        
        // 키보드 이벤트 감지 (개선된 버전)
        const activeKeys = new Set();
        
        document.addEventListener('keydown', (e) => {
            const keys = ['w', 'a', 's', 'd', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            const key = e.key.toLowerCase();
            
            if (keys.includes(key) || keys.includes(e.key)) {
                if (state.cameraMode === 'fly') {
                    // 새로운 키 입력이면 추적 시작
                    if (!activeKeys.has(key) && !activeKeys.has(e.key)) {
                        activeKeys.add(key);
                        cameraInteractionTracker.startInteraction('fly_keyboard');
                    }
                }
            }
        });
        
        document.addEventListener('keyup', (e) => {
            const keys = ['w', 'a', 's', 'd', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
            const key = e.key.toLowerCase();
            
            if (keys.includes(key) || keys.includes(e.key)) {
                activeKeys.delete(key);
                activeKeys.delete(e.key);
                
                // 모든 키가 떼어졌을 때만 세션 종료하지 않고, 
                // 비활성 타이머가 자동으로 종료하도록 둠
                // 이렇게 하면 키를 연속으로 누를 때 하나의 세션으로 처리됨
            }
        });
    
        if (state.cameraMode === 'anim') {
            activePose.copy(animCamera.update(controller.frame, 0));
        } else {
            activePose.copy(userStart);
        }
    
        orbitCamera.attach(activePose, false);
        flyCamera.attach(activePose, false);
    
        let transitionTimer = 0;
        const prevPose = new Pose();
        let prevCamera = null;
        let prevCameraMode = 'orbit';
    
        // doReset 함수 정의 (initialize 함수 내부)
        const doReset = (pose) => {
            
            if (animCamera) {
                const currentPos = entity.getPosition();
                const startPose = new Pose();
                startPose.position.copy(currentPos);
                startPose.look(currentPos, bbox.center);
                
                const newAnimTrack = createRotateTrack(startPose, bbox.center);
                animCamera = AnimController.fromTrack(newAnimTrack);
                
                orbitCamera.detach();
                flyCamera.detach();
                
                state.animationDuration = animCamera.cursor.duration;
                state.cameraMode = 'anim';
                state.animationPaused = false;
                
                animCamera.cursor.reset(animCamera.cursor.duration, animCamera.cursor.loopMode);
                activePose.copy(startPose);
                
            }
        };
    
        // inputEvent 핸들러 (initialize 함수 내부)
        events.on('inputEvent', async (eventName, event) => {
            switch (eventName) {
                case 'frame':
                    doReset(framePose);
                    
                    // 카메라 프레임 이벤트 전송
                    if (window.analytics) {
                        window.analytics.trackEvent('camera_frame', {
                            cameraMode: state.cameraMode,
                            cameraPosition: {
                                x: activePose.position.x,
                                y: activePose.position.y,
                                z: activePose.position.z
                            }
                        });
                    }
                    break;
                case 'reset':
                    doReset(resetPose);
                    
                    // 카메라 리셋 이벤트 전송
                    if (window.analytics) {
                        window.analytics.trackEvent('camera_reset', {
                            cameraMode: state.cameraMode,
                            resetType: 'manual'
                        });
                    }
                    break;
                case 'cancel':
                case 'interrupt':
                    if (state.cameraMode === 'anim') {
                        
                        const currentOrbitPose = new Pose();
                        currentOrbitPose.position.copy(activePose.position);
                        currentOrbitPose.look(activePose.position, bbox.center);
                        
                        state.cameraMode = 'orbit';
                        orbitCamera.attach(currentOrbitPose, false);
                        
                    }
                    break;
                case 'dblclick': {
                    // 더블클릭 처리 코드...
                    break;
                }
            }
        });
    
        // 카메라 모드 변경 핸들러 (initialize 함수 내부)
        events.on('cameraMode:changed', (value, prev) => {
            
            prevCameraMode = prev;
            prevPose.copy(activePose);
            
            if (prevCamera) {
                prevCamera.detach();
            }
            prevCamera = getCamera(prev);

            switch (value) {
                case 'orbit': {
                    
                    const orbitPose = new Pose();
                    orbitPose.position.copy(activePose.position);
                    orbitPose.look(activePose.position, bbox.center);
                    
                    orbitCamera.attach(orbitPose, false);
                    
                    break;
                }
                case 'fly': {
                    flyCamera.attach(activePose, false);
                    break;
                }
                case 'anim': {
                    orbitCamera.detach();
                    flyCamera.detach();
                    break;
                }
            }

            if (!state.snap) {
                transitionTimer = 0;
            }
            state.snap = false;
            
            // 카메라 모드 변경 이벤트 전송
            if (window.analytics) {
                window.analytics.trackEvent('camera_mode_change', {
                    fromMode: prev,
                    toMode: value,
                    cameraPosition: {
                        x: activePose.position.x,
                        y: activePose.position.y,
                        z: activePose.position.z
                    },
                    distance: activePose.position.distance(bbox.center)
                });
            }
            
        });
        
        // 카메라 인터랙션 종료 함수를 외부에서 호출 가능하도록 window에 노출
        window.endCameraInteraction = () => {
            try {
                cameraInteractionTracker.endInteraction();
            } catch (error) {
                console.warn('Failed to end camera interaction (non-critical):', error);
            }
        };

         // 3. 더 부드러운 제한을 위한 개선된 업데이트 로직
        app.on('update', (deltaTime) => {
            if (app.xr.active) {
                return;
            }

            controller.update(deltaTime, state, activePose.distance);

            if (state.cameraMode === 'fly') {
                events.fire('touchJoystickUpdate', controller.joystick.base, controller.joystick.stick);
            }

            const dt = state.cameraMode === 'anim' ?
                (state.animationPaused ? 0 : deltaTime * transitionTimer) :
                deltaTime;

            const newPose = getCamera(state.cameraMode).update(controller.frame, dt);
            
            // *** Fly 모드에서 개선된 바운더리 체크 ***
            if (state.cameraMode === 'fly') {
                const center = bbox.center;
                const radius = bbox.halfExtents.length();
                const margin = 1.0; // 마진을 좀 더 크게 설정
                const minDistance = radius + margin;
                const currentDistance = newPose.position.distance(center);
                
                if (currentDistance < minDistance) {
                    
                    // 현재 이동 방향을 유지하면서 경계에서 멈추기
                    const directionFromCenter = new Vec3();
                    directionFromCenter.sub2(newPose.position, center).normalize();
                    
                    // 경계 지점으로 위치 조정
                    const boundaryPosition = new Vec3();
                    boundaryPosition.copy(directionFromCenter).mulScalar(minDistance).add(center);
                    
                    newPose.position.copy(boundaryPosition);
                    
                }
            }
            
            pose.copy(newPose);

            // 카메라 전환 시 블렌딩
            if (transitionTimer < 1) {
                transitionTimer = Math.min(1, transitionTimer + deltaTime);
                if (transitionTimer < 1 && prevCamera) {
                    pose.lerp(prevPose, pose, easeOut(transitionTimer));
                }
            }

            activePose.copy(pose);
            entity.setPosition(activePose.position);
            entity.setEulerAngles(activePose.angles);

            if (state.cameraMode === 'anim') {
                state.animationTime = animCamera.cursor.value;
            }
        });

        // (중복 핸들러 제거됨 - 위의 584번째 줄 핸들러에 통합)

        events.on('setAnimationTime', (time) => {
            if (animCamera) {
                animCamera.cursor.value = time;

                // switch to animation camera if we're not already there
                if (state.cameraMode !== 'anim') {
                    state.cameraMode = 'anim';
                }
                
                // 애니메이션 시간 설정 이벤트 전송
                if (window.analytics) {
                    window.analytics.trackEvent('animation_time_set', {
                        animationTime: time,
                        animationDuration: state.animationDuration,
                        progress: time / state.animationDuration
                    });
                }
            }
        });

        events.on('resetAnimation', () => {
            if (animCamera) {
                animCamera.cursor.reset(animCamera.cursor.duration, animCamera.cursor.loopMode);
                
                // 애니메이션 리셋 이벤트 전송
                if (window.analytics) {
                    window.analytics.trackEvent('animation_reset', {
                        animationDuration: state.animationDuration,
                        cameraMode: state.cameraMode
                    });
                }
            }
        });

        // pick orbit camera focus point on double click
        let picker = null;
        events.on('inputEvent', async (eventName, event) => {
            switch (eventName) {
                case 'dblclick': {
                    if (!picker) {
                        picker = new Picker(app, entity);
                    }
                    const result = await picker.pick(event.offsetX, event.offsetY);
                    if (result) {
                        if (state.cameraMode !== 'orbit') {
                            state.snap = true;
                            state.cameraMode = 'orbit';
                        }

                        // snap distance of focus to picked point to interpolate rotation only
                        activePose.distance = activePose.position.distance(result);
                        orbitCamera.attach(activePose, false);

                        orbitCamera.attach(pose.look(activePose.position, result), true);
                    }
                    break;
                }
            }
        });

        // initialize the camera entity to initial position and kick off the
        // first scene sort (which usually happens during render)
        entity.setPosition(activePose.position);
        entity.setEulerAngles(activePose.angles);
        gsplat?.instance?.sort(entity);

        // handle gsplat sort updates
        gsplat?.instance?.sorter?.on('updated', () => {
            // request frame render when sorting changes
            app.renderNextFrame = true;

            if (!state.readyToRender) {
                // we're ready to render once the first sort has completed
                state.readyToRender = true;

                // wait for the first valid frame to complete rendering
                const frameHandle = app.on('frameend', () => {
                    frameHandle.off();

                    events.fire('firstFrame');

                    // emit first frame event on window
                    window.firstFrame?.();
                });
            }
        });

        // 1. 구형(Sphere) 기반 바운더리 체크로 변경
        const isPositionInsideBounds = (position, boundingBox, margin = 0.5) => {
            const center = boundingBox.center;
            const radius = boundingBox.halfExtents.length(); // 바운딩 스피어 반지름
            const distance = position.distance(center);
            
            return distance < (radius + margin);
        };
        // 2. 안전한 위치로 조정하는 함수 - 더 자연스러운 방식
        const getSafePosition = (desiredPosition, currentPosition, boundingBox, margin = 0.5) => {
            const center = boundingBox.center;
            const radius = boundingBox.halfExtents.length();
            const minDistance = radius + margin;
            
            const distanceToCenter = desiredPosition.distance(center);
            
            // 바운더리 밖에 있으면 그대로 허용
            if (distanceToCenter >= minDistance) {
                return desiredPosition.clone();
            }
            
            // 바운더리 안쪽으로 들어가려고 하면, 현재 위치에서 중심으로의 방향을 유지하면서
            // 최소 거리 지점으로 위치 조정
            
            // 현재 위치에서 중심으로의 방향 벡터 계산
            const directionFromCenter = new Vec3();
            directionFromCenter.sub2(currentPosition, center);
            
            // 방향이 없으면 (중심에 너무 가까우면) 기본 방향 사용
            if (directionFromCenter.length() < 0.01) {
                directionFromCenter.set(0, 0, 1); // 기본적으로 Z축 방향
            } else {
                directionFromCenter.normalize();
            }
            
            // 최소 거리 지점으로 위치 설정
            const safePosition = new Vec3();
            safePosition.copy(directionFromCenter).mulScalar(minDistance).add(center);
            
            
            return safePosition;
        };
    }
}

export { Viewer };
